if (!customElements.get('opsando-variant-picker')) {
  customElements.define(
    'opsando-variant-picker',
    class OpsandoVariantPicker extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.data = JSON.parse(this.querySelector('[data-picker-data]').textContent);
        this.tooltipTemplate = this.querySelector('[data-picker-tooltip]');
        this.optionKeys = this.data.options.map((name) => name.toLowerCase());
        this.popularByOption = this.optionKeys.map((key, index) => this.getLeadingValue(index));
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.pendingText = new WeakMap();
        this.fadeTimers = new WeakMap();

        this.abortController = new AbortController();
        const { signal } = this.abortController;

        document.addEventListener(
          'change',
          (event) => {
            if (this.isVariantChange(event)) this.refresh(true);
          },
          { capture: true, signal }
        );
        document.addEventListener('click', (event) => this.onClick(event), { signal });
        document.addEventListener(
          'keydown',
          (event) => {
            if (event.key === 'Escape') this.closeTooltips(null);
          },
          { signal }
        );

        this.refresh(false);
      }

      disconnectedCallback() {
        this.abortController.abort();
      }

      format(template, values) {
        return template.replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));
      }

      getLeadingValue(optionIndex) {
        const totals = {};
        this.data.variants.forEach((variant) => {
          if (!variant.inventory_management) return;
          const value = variant.options[optionIndex];
          totals[value] = (totals[value] || 0) + Math.max(variant.inventory_quantity || 0, 0);
        });

        const values = Object.keys(totals).sort((a, b) => totals[b] - totals[a]);
        if (values.length < 2 || totals[values[0]] <= 0) return null;

        const runnerUp = totals[values[1]];
        const margin = runnerUp > 0 ? (totals[values[0]] - runnerUp) / runnerUp : Infinity;
        return margin >= this.data.popularMargin ? { value: values[0], margin } : null;
      }

      getPicker() {
        const pickers = document.querySelectorAll(`variant-selects[data-section="${this.sectionId}"]`);
        return Array.from(pickers).find((picker) => picker.style.display !== 'none') || null;
      }

      getSelection(groups) {
        return groups.map((group) => {
          const input = group.querySelector('input[type="radio"]:checked') || group.querySelector('select');
          return input ? input.value : null;
        });
      }

      isVariantChange(event) {
        const target = event.target;
        if (target.closest(`variant-selects[data-section="${this.sectionId}"]`)) return true;
        return target.name === 'id' && target.form === document.getElementById(`product-form-${this.sectionId}`);
      }

      ensureNote(key, anchor, className) {
        const notes = document.querySelectorAll(`[data-opsando-note="${this.sectionId}-${key}"]`);
        for (let i = 1; i < notes.length; i++) notes[i].remove();

        let note = notes[0];
        if (!note) {
          note = document.createElement('p');
          note.className = className;
          note.dataset.opsandoNote = `${this.sectionId}-${key}`;
        }
        if (anchor.nextElementSibling !== note) anchor.after(note);
        return note;
      }

      setNoteText(note, text) {
        const target = this.pendingText.has(note) ? this.pendingText.get(note) : note.textContent;
        if (target === text) return;

        if (this.reducedMotion.matches) {
          note.textContent = text;
          return;
        }

        this.pendingText.set(note, text);
        note.classList.add('is-updating');
        clearTimeout(this.fadeTimers.get(note));
        this.fadeTimers.set(
          note,
          setTimeout(() => {
            note.textContent = this.pendingText.get(note);
            this.pendingText.delete(note);
            note.classList.remove('is-updating');
          }, parseFloat(getComputedStyle(note).transitionDuration) * 1000)
        );
      }

      addTooltips(groups) {
        groups.forEach((group, index) => {
          const tip = this.data.infoTooltips[this.optionKeys[index]];
          const row = group.querySelector('.form__label > .flex');
          if (!tip || !row || row.querySelector('.opsando-vp-info')) return;

          const tooltip = this.tooltipTemplate.content.firstElementChild.cloneNode(true);
          const button = tooltip.querySelector('button');
          const bubble = tooltip.querySelector('.opsando-vp-info__bubble');
          bubble.id = `OpsandoTooltip-${this.sectionId}-${index}`;
          bubble.textContent = tip;
          button.setAttribute('aria-controls', bubble.id);
          button.setAttribute('aria-label', this.format(this.data.text.about, { option: this.data.options[index] }));
          row.appendChild(tooltip);
        });
      }

      setTooltipOpen(tooltip, open) {
        tooltip.querySelector('.opsando-vp-info__bubble').hidden = !open;
        tooltip.querySelector('button').setAttribute('aria-expanded', String(open));
      }

      closeTooltips(except) {
        document.querySelectorAll('.opsando-vp-info').forEach((tooltip) => {
          if (tooltip !== except) this.setTooltipOpen(tooltip, false);
        });
      }

      onClick(event) {
        const tooltip = event.target.closest('.opsando-vp-info');
        this.closeTooltips(tooltip);
        if (tooltip && event.target.closest('.opsando-vp-info__button')) {
          this.setTooltipOpen(tooltip, tooltip.querySelector('.opsando-vp-info__bubble').hidden);
        }
      }

      updatePopularBadge(groups) {
        let best = null;
        groups.forEach((group, index) => {
          const candidate = this.popularByOption[index];
          if (!candidate || !group.classList.contains('product-form__input--button')) return;
          if (!best || candidate.margin > best.margin) best = { group, value: candidate.value, margin: candidate.margin };
        });

        groups.forEach((group) => {
          group.querySelectorAll('input[type="radio"]').forEach((input) => {
            const label = input.labels[0];
            if (!label) return;
            let badge = label.querySelector('.opsando-vp-badge');
            const shouldHaveBadge = Boolean(best) && group === best.group && input.value === best.value;

            if (shouldHaveBadge && !badge) {
              badge = document.createElement('span');
              badge.className = 'f-badge opsando-vp-badge';
              badge.textContent = this.data.text.popular;
              label.appendChild(badge);
            } else if (!shouldHaveBadge && badge) {
              badge.remove();
            }
          });
        });
      }

      announce(message) {
        let statusRegion = document.querySelector('[data-opsando-status]');
        if (!statusRegion) {
          statusRegion = document.createElement('div');
          statusRegion.className = 'visually-hidden';
          statusRegion.setAttribute('role', 'status');
          statusRegion.setAttribute('aria-live', 'polite');
          statusRegion.dataset.opsandoStatus = '';
          document.body.appendChild(statusRegion);
        }
        if (statusRegion.textContent !== message) statusRegion.textContent = message;
      }

      refresh(shouldAnnounce) {
        const picker = this.getPicker();
        if (!picker) return;

        const groups = Array.from(picker.querySelectorAll(':scope > .product-form__input'));
        const selection = this.getSelection(groups);
        const conditionIndex = this.optionKeys.indexOf(this.data.conditionOption);
        const conditionGroup = groups[conditionIndex];
        const selectionText = selection.filter(Boolean).join(' · ');

        const conditionText = conditionGroup
          ? this.data.conditionHints[(selection[conditionIndex] || '').toLowerCase()] || ''
          : '';
        const summaryText = this.format(this.data.text.selected, { selection: selectionText });

        if (conditionGroup) this.setNoteText(this.ensureNote('condition', conditionGroup, 'opsando-vp-note text-sm'), conditionText);
        this.setNoteText(this.ensureNote('summary', picker, 'opsando-vp-summary font-body-bold'), summaryText);

        this.addTooltips(groups);
        this.updatePopularBadge(groups);

        if (shouldAnnounce) this.announce([summaryText, conditionText].filter(Boolean).join(' '));
      }
    }
  );
}
